import { useState, useEffect, useRef, type SetStateAction, type Dispatch } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '@/components/ui/dropdown-menu';
import { Search, X, Flag, ChevronDown, Radio, Workflow, Lock, User, Stamp } from 'lucide-react';
import { ProspectFilters } from './types';
import { PIPELINE_OPTIONS } from './pipelineOptions';
import { POST_CLOSING_QUEUE_LABELS, type PostClosingQueueStage } from './postClosingQueueStage';
import { useAvailableMilestones, MilestoneCount } from './use-available-milestones';
import { useAvailableChannels, ChannelCount } from './use-available-channels';
import { useAvailableLoanOfficers } from './use-available-loan-officers';

interface ProspectFiltersProps {
  filters: ProspectFilters;
  onFiltersChange: Dispatch<SetStateAction<ProspectFilters>>;
  onPipelineChange: (pipeline: 'post_closing' | 'mers_audit' | null) => void;
  organizationId: string | undefined;
  hideLoanOfficerFilter?: boolean;
}

export function ProspectFiltersComponent({
  filters,
  onFiltersChange,
  onPipelineChange,
  organizationId,
  hideLoanOfficerFilter = false
}: ProspectFiltersProps) {
  // Local state for immediate UI updates
  const [localSearch, setLocalSearch] = useState(filters.searchQuery || '');
  const searchRef = useRef<HTMLInputElement>(null);

  // Fetch available milestones and channels (channels respects excluded folders)
  const { milestones } = useAvailableMilestones(organizationId);
  const { channels } = useAvailableChannels(organizationId, filters.excludedFolders);
  const { officers, unassignedCount } = useAvailableLoanOfficers(
    hideLoanOfficerFilter ? undefined : organizationId
  );

  const selectedOfficerLabel = (() => {
    const id = filters.loanOfficerId;
    if (!id || id === 'all') {
      return null;
    }
    if (id === 'unassigned') {
      return 'Unassigned';
    }
    return officers.find(o => o.value === id)?.label ?? null;
  })();

  const handleOfficerChange = (value: string) => {
    onFiltersChange(prev => ({
      ...prev,
      loanOfficerId: value === 'all' ? 'all' : value
    }));
  };

  // Sync local search to parent using functional update to avoid stale filter references
  // Only depends on filters.searchQuery (not entire filters object) to prevent firing on every filter change
  useEffect(() => {
    if (localSearch !== filters.searchQuery) {
      onFiltersChange(prev => ({ ...prev, searchQuery: localSearch }));
    }
  }, [localSearch, filters.searchQuery, onFiltersChange]);

  // Sync local state if filters change externally (e.g., clear button, programmatic change)
  useEffect(() => {
    if (filters.searchQuery !== localSearch) {
      setLocalSearch(filters.searchQuery || '');
    }
    // Only depend on filters.searchQuery to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.searchQuery]);

  const handleClearSearch = () => {
    setLocalSearch('');
    onFiltersChange(prev => ({ ...prev, searchQuery: '' }));
    searchRef.current?.focus();
  };

  const handleToggleMilestone = (milestone: string) => {
    onFiltersChange(prev => {
      const excluded = prev.excludedMilestones || [];
      if (excluded.includes(milestone)) {
        return { ...prev, excludedMilestones: excluded.filter(m => m !== milestone) };
      } else {
        return { ...prev, excludedMilestones: [...excluded, milestone] };
      }
    });
  };

  const handleShowAllMilestones = () => {
    onFiltersChange(prev => ({ ...prev, excludedMilestones: [] }));
  };

  const handleHideAllMilestones = () => {
    onFiltersChange(prev => ({ ...prev, excludedMilestones: milestones.map(m => m.milestone) }));
  };

  const handleToggleChannel = (channel: string) => {
    onFiltersChange(prev => {
      const excluded = prev.excludedChannels || [];
      if (excluded.includes(channel)) {
        return { ...prev, excludedChannels: excluded.filter(c => c !== channel) };
      } else {
        return { ...prev, excludedChannels: [...excluded, channel] };
      }
    });
  };

  const handleShowAllChannels = () => {
    onFiltersChange(prev => ({ ...prev, excludedChannels: [] }));
  };

  const handleHideAllChannels = () => {
    onFiltersChange(prev => ({ ...prev, excludedChannels: channels.map(c => c.channel) }));
  };

  const handlePipelineChange = (pipelineId: string) => {
    onPipelineChange(
      pipelineId === 'post_closing' || pipelineId === 'mers_audit'
        ? (pipelineId as 'post_closing' | 'mers_audit')
        : null
    );
  };

  const handleMersStatusChange = (value: string) => {
    onFiltersChange(prev => ({
      ...prev,
      mersStatus: value === 'all' ? null : (value as 'blank' | 'transferred')
    }));
  };

  const handleLockStatusChange = (status: string) => {
    if (status === 'all') {
      onFiltersChange(prev => ({ ...prev, lockStatus: null }));
    } else {
      onFiltersChange(prev => ({ ...prev, lockStatus: status as 'Locked' | 'NotLocked' | 'Expired' }));
    }
  };

  const handleDeliveryStatusChange = (status: string) => {
    if (status === 'all') {
      onFiltersChange(prev => ({ ...prev, deliveryStatus: null, queueStage: null }));
    } else {
      onFiltersChange(prev => ({ ...prev, deliveryStatus: status as 'FundedNotDelivered' | 'FundedNotReadyToShip' | 'Shipped' | 'Delivered', queueStage: null }));
    }
  };

  const handleQueueStageChange = (stage: string) => {
    if (stage === 'all') {
      onFiltersChange(prev => ({ ...prev, queueStage: null, deliveryStatus: null }));
    } else {
      onFiltersChange(prev => ({
        ...prev,
        queueStage: stage as PostClosingQueueStage,
        deliveryStatus: null
      }));
    }
  };

  // Get current pipeline label
  const currentPipeline = PIPELINE_OPTIONS.find(p => p.id === filters.pipeline);

  // Post-closing stage labels — queue stage (card click or dropdown) takes priority over delivery status
  const queueStageLabel = filters.pipeline === 'post_closing' && filters.queueStage
    ? POST_CLOSING_QUEUE_LABELS[filters.queueStage] || null
    : null;

  const postClosingStageLabel = queueStageLabel || (filters.pipeline === 'post_closing' && filters.deliveryStatus
    ? filters.deliveryStatus === 'FundedNotReadyToShip' ? 'Not Ready to Ship'
      : filters.deliveryStatus === 'FundedNotDelivered' ? 'Ready to Ship'
      : filters.deliveryStatus === 'Shipped' ? 'Shipped'
      : filters.deliveryStatus === 'Delivered' ? 'Delivered'
      : null
    : null);

  // Get current filter label (combining lock status and delivery status)
  const hasLockFilter = filters.lockStatus && filters.lockStatus !== 'all';
  const hasDeliveryFilter = filters.deliveryStatus && filters.deliveryStatus !== 'all';

  const statusFilterLabel = hasLockFilter || hasDeliveryFilter
    ? [
        filters.lockStatus === 'Locked' ? 'Locked'
          : filters.lockStatus === 'NotLocked' ? 'Not Locked'
          : filters.lockStatus === 'Expired' ? 'Expired'
          : null,
        filters.deliveryStatus === 'FundedNotReadyToShip' ? 'Funded - Not Ready to Ship'
          : filters.deliveryStatus === 'FundedNotDelivered' ? 'Funded - Ready to Deliver'
          : filters.deliveryStatus === 'Shipped' ? 'Shipped'
          : filters.deliveryStatus === 'Delivered' ? 'Delivered'
          : null
      ].filter(Boolean).join(' + ') || null
    : null;

  const finalStatusLabel = postClosingStageLabel || statusFilterLabel;

  // Post-closing stage colors — queue stage gets a distinct teal style
  const postClosingStageStyle = filters.pipeline === 'post_closing' && filters.queueStage
    ? 'bg-teal-600 hover:bg-teal-700'
    : filters.pipeline === 'post_closing' && filters.deliveryStatus
    ? filters.deliveryStatus === 'FundedNotReadyToShip' ? 'bg-gray-600 hover:bg-gray-700'
      : filters.deliveryStatus === 'FundedNotDelivered' ? 'bg-yellow-600 hover:bg-yellow-700'
      : filters.deliveryStatus === 'Shipped' ? 'bg-blue-600 hover:bg-blue-700'
      : filters.deliveryStatus === 'Delivered' ? 'bg-green-600 hover:bg-green-700'
      : ''
    : '';

  // Different colors for different lock statuses (delivery adds blue tint)
  const lockStatusStyle = filters.lockStatus === 'Locked' ? 'bg-green-600 hover:bg-green-700'
    : filters.lockStatus === 'Expired' ? 'bg-red-600 hover:bg-red-700'
    : filters.lockStatus === 'NotLocked' ? 'bg-gray-600 hover:bg-gray-700'
    : filters.deliveryStatus === 'Delivered' ? 'bg-blue-600 hover:bg-blue-700'
    : '';

  // Calculate visible milestone count
  const visibleMilestoneCount = milestones.length - (filters.excludedMilestones?.length || 0);
  const visibleChannelCount = channels.length - (filters.excludedChannels?.length || 0);

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 items-center [&>button]:h-auto [&>button]:min-h-9 [&>button]:max-w-full [&>button]:whitespace-normal [&>button]:text-left [&>button>svg]:shrink-0">
      <div className="relative min-w-40 max-w-sm flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={searchRef}
          type="text"
          aria-label="Search loans"
          placeholder="Search borrower, loan #, investor loan #, address, investor, loan officer..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-10 pr-10"
        />
        {localSearch && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={handleClearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Workflow Filter Dropdown (label renamed from "Pipeline" to avoid colliding
          with the Pipeline direction tab in the redesigned active Loan Center).
          Internal `filters.pipeline` field is unchanged. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={currentPipeline ? "default" : "outline"}
            size="sm"
            className={currentPipeline ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <Workflow className="h-4 w-4 mr-2" />
            {currentPipeline ? currentPipeline.label : 'Workflow'}
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Select Workflow</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup value={filters.pipeline || 'none'} onValueChange={handlePipelineChange}>
            <DropdownMenuRadioItem value="none">
              All Loans
            </DropdownMenuRadioItem>
            {PIPELINE_OPTIONS.map((option) => (
              <DropdownMenuRadioItem key={option.id} value={option.id}>
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* MERS status sub-filter — only in the MERS Transfer Date Audit workflow.
          Sits right next to Workflow; isolates blank (missing) vs transferred loans. */}
      {filters.pipeline === 'mers_audit' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={filters.mersStatus && filters.mersStatus !== 'all' ? 'default' : 'outline'}
              size="sm"
              className={filters.mersStatus && filters.mersStatus !== 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            >
              <Stamp className="h-4 w-4 mr-2" />
              {filters.mersStatus === 'blank' ? 'MERS: Blank' : filters.mersStatus === 'transferred' ? 'MERS: Not Blank' : 'MERS'}
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>MERS Transfer Date</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup value={filters.mersStatus || 'all'} onValueChange={handleMersStatusChange}>
              <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="blank">Blank (missing)</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="transferred">Not blank (transferred)</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Status Filter Dropdown (Lock Status + Delivery Status) — hidden in MERS audit
          (queue/lock/delivery status don't apply to a purchased-loan MERS audit). */}
      {filters.pipeline !== 'mers_audit' && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={finalStatusLabel ? "default" : "outline"}
            size="sm"
            className={postClosingStageStyle || lockStatusStyle}
          >
            <Lock className="h-4 w-4 mr-2" />
            {finalStatusLabel || 'Status'}
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {filters.pipeline === 'post_closing' ? (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">Post Closing Stage</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={filters.queueStage || 'all'} onValueChange={handleQueueStageChange}>
                <DropdownMenuRadioItem value="all">All Stages</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="funded">Not Ready to Ship</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="ready_to_ship">Ready to Ship</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="delivered">Delivered</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="conditions">Conditions</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="cleared">Cleared</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="pa_issued">PA Issued</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="finalized">Finalized</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </>
          ) : (
            <>
              {/* Lock Status Section */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">Lock Status</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={filters.lockStatus || 'all'} onValueChange={handleLockStatusChange}>
                <DropdownMenuRadioItem value="all">All Loans</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="Locked">Locked</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="Expired">Expired</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="NotLocked">Not Locked</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>

              <DropdownMenuSeparator />

              {/* Delivery Status Section */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">Delivery Status</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={filters.deliveryStatus || 'all'} onValueChange={handleDeliveryStatusChange}>
                <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="FundedNotReadyToShip">Funded - Not Ready to Ship</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="FundedNotDelivered">Funded - Ready to Deliver</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="Delivered">Delivered to Investor</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      )}

      {/* Milestone Filter Dropdown — active (All Loans) workflow only */}
      {!filters.pipeline && milestones.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Flag className="h-4 w-4 mr-2" />
              Milestone
              {filters.excludedMilestones && filters.excludedMilestones.length > 0 && (
                <span className="ml-1 text-muted-foreground">
                  ({visibleMilestoneCount}/{milestones.length})
                </span>
              )}
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
            <DropdownMenuLabel>Show Milestones</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {visibleMilestoneCount < milestones.length && (
              <DropdownMenuCheckboxItem
                checked={false}
                onCheckedChange={handleShowAllMilestones}
                onSelect={(e) => e.preventDefault()}
              >
                Show all
              </DropdownMenuCheckboxItem>
            )}
            {visibleMilestoneCount > 0 && (
              <DropdownMenuCheckboxItem
                checked={false}
                onCheckedChange={handleHideAllMilestones}
                onSelect={(e) => e.preventDefault()}
              >
                Hide all
              </DropdownMenuCheckboxItem>
            )}
            <DropdownMenuSeparator />
            {milestones.map(({ milestone, count }: MilestoneCount) => {
              const isIncluded = !filters.excludedMilestones?.includes(milestone);
              return (
                <DropdownMenuCheckboxItem
                  key={milestone}
                  checked={isIncluded}
                  onCheckedChange={() => handleToggleMilestone(milestone)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {milestone} ({count.toLocaleString()})
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Channel Filter Dropdown — hidden in post-closing mode */}
      {!filters.pipeline && channels.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Radio className="h-4 w-4 mr-2" />
              Channel
              {filters.excludedChannels && filters.excludedChannels.length > 0 && (
                <span className="ml-1 text-muted-foreground">
                  ({visibleChannelCount}/{channels.length})
                </span>
              )}
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
            <DropdownMenuLabel>Show Channels</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {visibleChannelCount < channels.length && (
              <DropdownMenuCheckboxItem
                checked={false}
                onCheckedChange={handleShowAllChannels}
                onSelect={(e) => e.preventDefault()}
              >
                Show all
              </DropdownMenuCheckboxItem>
            )}
            {visibleChannelCount > 0 && (
              <DropdownMenuCheckboxItem
                checked={false}
                onCheckedChange={handleHideAllChannels}
                onSelect={(e) => e.preventDefault()}
              >
                Hide all
              </DropdownMenuCheckboxItem>
            )}
            <DropdownMenuSeparator />
            {channels.map(({ channel, count }: ChannelCount) => {
              const isIncluded = !filters.excludedChannels?.includes(channel);
              return (
                <DropdownMenuCheckboxItem
                  key={channel}
                  checked={isIncluded}
                  onCheckedChange={() => handleToggleChannel(channel)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {channel} ({count.toLocaleString()})
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Officer Filter — hidden in post-closing mode (same scope as Milestone/Channel). */}
      {!hideLoanOfficerFilter && !filters.pipeline && officers.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant={selectedOfficerLabel ? 'default' : 'outline'} size="sm">
              <User className="h-4 w-4 mr-2" />
              {selectedOfficerLabel ?? 'Officer'}
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
            <DropdownMenuLabel>Filter by Loan Officer</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={filters.loanOfficerId || 'all'}
              onValueChange={handleOfficerChange}
            >
              <DropdownMenuRadioItem value="all">All Officers</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="unassigned">
                Unassigned ({unassignedCount.toLocaleString()})
              </DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              {officers.map(o => (
                <DropdownMenuRadioItem key={o.value} value={o.value}>
                  {o.label} ({o.count.toLocaleString()})
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {localSearch && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleClearSearch}
          className="flex items-center gap-2"
        >
          <X className="h-4 w-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
